import {
  Bell,
  Bookmark,
  Briefcase,
  Building2,
  Calendar,
  FileText,
  Gavel,
  Handshake,
  Home,
  Inbox,
  LayoutDashboard,
  Scale,
  Settings,
  User,
  UserCircle,
  UserPlus,
  Users,
  Zap,
  type LucideIcon
} from 'lucide-react';

import { LEGACY_USER_ROLES } from '@shared/auth/roles';

import type { UserRole } from '@/types/user';

export interface NavItem {
  /** Stable id — the sidebar's active-item key. Never derive it from the path. */
  id: string;
  path: string;
  /** i18next key, resolved at render so a language switch re-labels without a remount. */
  labelKey: string;
  /** i18next key for the sidebar group heading. */
  groupKey: string;
  icon: LucideIcon;
  roles: readonly UserRole[];
}

/**
 * ONE nav model. The sidebar, the command palette and the route table all read it.
 * They used to be three hand-maintained tables and had already drifted — a page reachable
 * from the palette but missing from the sidebar, and vice versa.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    path: '/dashboard',
    labelKey: 'nav.dashboard',
    groupKey: 'groups.overview',
    icon: Home,
    roles: LEGACY_USER_ROLES
  },

  {
    id: 'buyer-properties',
    path: '/buyer/properties',
    labelKey: 'nav.browseProperties',
    groupKey: 'groups.buyer',
    icon: Building2,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'buyer-saved',
    path: '/buyer/saved',
    labelKey: 'nav.saved',
    groupKey: 'groups.buyer',
    icon: Bookmark,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'buyer-alerts',
    path: '/buyer/alerts',
    labelKey: 'nav.alerts',
    groupKey: 'groups.buyer',
    icon: Zap,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'buyer-enquiries',
    path: '/buyer/enquiries',
    labelKey: 'nav.enquiries',
    groupKey: 'groups.buyer',
    icon: Handshake,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'buyer-negotiations',
    path: '/buyer/negotiations',
    labelKey: 'nav.negotiations',
    groupKey: 'groups.buyer',
    icon: Briefcase,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'buyer-legal-requests',
    path: '/buyer/legal-requests',
    labelKey: 'nav.legalRequests',
    groupKey: 'groups.buyer',
    icon: Scale,
    roles: LEGACY_USER_ROLES
  },

  {
    id: 'my-properties',
    path: '/properties',
    labelKey: 'nav.myProperties',
    groupKey: 'groups.seller',
    icon: Building2,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'seller-enquiries',
    path: '/seller/enquiries',
    labelKey: 'nav.enquiries',
    groupKey: 'groups.seller',
    icon: Handshake,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'seller-buyer-enquiries',
    path: '/seller/buyer-enquiries',
    labelKey: 'nav.buyerEnquiries',
    groupKey: 'groups.seller',
    icon: Inbox,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'seller-activity',
    path: '/seller/activity',
    labelKey: 'nav.activity',
    groupKey: 'groups.seller',
    icon: FileText,
    roles: LEGACY_USER_ROLES
  },

  {
    id: 'broker-properties',
    path: '/broker/properties',
    labelKey: 'nav.properties',
    groupKey: 'groups.broker',
    icon: Building2,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'broker-saved',
    path: '/broker/saved',
    labelKey: 'nav.saved',
    groupKey: 'groups.broker',
    icon: Bookmark,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'broker-enquiries',
    path: '/broker/enquiries',
    labelKey: 'nav.enquiries',
    groupKey: 'groups.broker',
    icon: Handshake,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'broker-deals',
    path: '/broker/deals',
    labelKey: 'nav.deals',
    groupKey: 'groups.broker',
    icon: Briefcase,
    roles: LEGACY_USER_ROLES
  },

  {
    id: 'lawyer-dashboard',
    path: '/lawyer/dashboard',
    labelKey: 'nav.dashboard',
    groupKey: 'groups.overview',
    icon: LayoutDashboard,
    roles: ['lawyer']
  },
  {
    id: 'lawyer-properties',
    path: '/lawyer/properties',
    labelKey: 'nav.properties',
    groupKey: 'groups.assignments',
    icon: Building2,
    roles: ['lawyer']
  },
  {
    id: 'lawyer-certificates',
    path: '/lawyer/certificates',
    labelKey: 'nav.certificates',
    groupKey: 'groups.assignments',
    icon: FileText,
    roles: ['lawyer']
  },
  {
    id: 'lawyer-calendar',
    path: '/lawyer/calendar',
    labelKey: 'nav.calendar',
    groupKey: 'groups.assignments',
    icon: Calendar,
    roles: ['lawyer']
  },
  {
    id: 'lawyer-profile',
    path: '/lawyer/profile',
    labelKey: 'nav.profile',
    groupKey: 'groups.account',
    icon: User,
    roles: ['lawyer']
  },
  {
    id: 'lawyer-notifications',
    path: '/lawyer/notifications',
    labelKey: 'nav.notifications',
    groupKey: 'groups.account',
    icon: Bell,
    roles: ['lawyer']
  },

  {
    id: 'notifications',
    path: '/notifications',
    labelKey: 'nav.notifications',
    groupKey: 'groups.account',
    icon: Bell,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'account',
    path: '/account',
    labelKey: 'nav.account',
    groupKey: 'groups.account',
    icon: Settings,
    roles: LEGACY_USER_ROLES
  },
  {
    id: 'profile-details',
    path: '/buyer/profile',
    labelKey: 'nav.profile',
    groupKey: 'groups.account',
    icon: User,
    roles: LEGACY_USER_ROLES
  },

  {
    id: 'admin-dashboard',
    path: '/admin/dashboard',
    labelKey: 'nav.dashboard',
    groupKey: 'groups.overview',
    icon: LayoutDashboard,
    roles: ['admin']
  },
  {
    id: 'admin-properties',
    path: '/admin/properties',
    labelKey: 'nav.properties',
    groupKey: 'groups.properties',
    icon: Building2,
    roles: ['admin']
  },
  {
    id: 'admin-enquiries',
    path: '/admin/enquiries',
    labelKey: 'nav.enquiries',
    groupKey: 'groups.properties',
    icon: Handshake,
    roles: ['admin']
  },
  {
    id: 'admin-deals',
    path: '/admin/deals',
    labelKey: 'nav.deals',
    groupKey: 'groups.properties',
    icon: Briefcase,
    roles: ['admin']
  },
  {
    id: 'admin-legal-requests',
    path: '/admin/legal-requests',
    labelKey: 'nav.legalRequests',
    groupKey: 'groups.properties',
    icon: Gavel,
    roles: ['admin']
  },
  {
    id: 'admin-sellers',
    path: '/admin/sellers',
    labelKey: 'nav.sellers',
    groupKey: 'groups.users',
    icon: Users,
    roles: ['admin']
  },
  {
    id: 'admin-lawyers',
    path: '/admin/lawyers',
    labelKey: 'nav.lawyers',
    groupKey: 'groups.users',
    icon: Scale,
    roles: ['admin']
  },
  {
    id: 'admin-buyers',
    path: '/admin/buyers',
    labelKey: 'nav.buyers',
    groupKey: 'groups.users',
    icon: UserCircle,
    roles: ['admin']
  },
  {
    id: 'admin-property-managers',
    path: '/admin/property-managers',
    labelKey: 'nav.propertyManagers',
    groupKey: 'groups.users',
    icon: UserPlus,
    roles: ['admin']
  },
  {
    id: 'admin-buyer-enquiries',
    path: '/admin/buyer-enquiries',
    labelKey: 'nav.buyerEnquiries',
    groupKey: 'groups.users',
    icon: Inbox,
    roles: ['admin']
  },
  {
    id: 'admin-notifications',
    path: '/admin/notifications',
    labelKey: 'nav.notifications',
    groupKey: 'groups.system',
    icon: Bell,
    roles: ['admin']
  }
];

export const navItemsForRole = (role: UserRole | undefined): NavItem[] =>
  role ? NAV_ITEMS.filter((item) => item.roles.includes(role)) : [];

/**
 * Longest matching path wins, so `/broker/properties/12` stays on `broker-properties`
 * instead of falling back to the dashboard the way the hand-written if-chain did.
 */
export function activeNavId(pathname: string, role: UserRole | undefined): string {
  const candidates = navItemsForRole(role)
    .filter((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length);
  return candidates[0]?.id ?? 'home';
}
